import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { SSEMessage } from '@/_shared/types';

/**
 * When to read a turn's assist card back after `turn_complete`. The server picks the card after the
 * turn, so the stream has closed by then, and it drops a card not ready within 15 s
 * (`ASSIST_RUN_DEADLINE_MS` in `src/agent/assist-run.ts`; not imported, that module pulls the model SDK).
 * Early reads show a quick card quickly; the last one lands just after that deadline. A read that finds
 * the card, or finds the run settled with none, ends the turn's reads.
 */
export const ASSIST_READBACK_DELAYS_MS = [2_000, 4_000, 8_000, 16_000] as const;

/**
 * How long after a turn ended with assist pending a chat opened from history still reads its card back:
 * the server's 15 s deadline, plus room for clock skew between the server's stamp and this browser.
 */
export const ASSIST_HISTORY_READBACK_WINDOW_MS = 30_000;

/** Assist cards written after `afterSeq` that the transcript does not hold yet. */
export function newAssistCards(
  history: readonly SSEMessage[],
  current: readonly SSEMessage[],
  afterSeq: number,
): SSEMessage[] {
  const held = new Set(current.flatMap((message) => (message.seq == null ? [] : [message.seq])));
  return history.filter(
    (message) =>
      message.type === 'ui_message' &&
      message.uiType === 'assist_suggestion' &&
      message.seq != null &&
      message.seq > afterSeq &&
      !held.has(message.seq),
  );
}

/**
 * The `turn_complete` seq of a chat's last turn when its assist card may still be written: the turn
 * ended with `assistPending`, its run has not settled, no card or newer turn follows it, and it ended
 * within `ASSIST_HISTORY_READBACK_WINDOW_MS` of `now`. Null otherwise, including for an older event that
 * has no `assistPendingAt`.
 */
export function pendingAssistTurnSeq(history: readonly SSEMessage[], now: number): number | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const message = history[i]!;
    if (message.type === 'turn_start') return null;
    if (message.type === 'ui_message' && message.uiType === 'assist_suggestion') return null;
    if (message.type !== 'turn_complete') continue;
    if (message.assistPending !== true || message.assistSettled === true) return null;
    if (message.seq == null || message.assistPendingAt == null) return null;
    return now - message.assistPendingAt <= ASSIST_HISTORY_READBACK_WINDOW_MS ? message.seq : null;
  }
  return null;
}

/** Reads one finished turn's card back; `cancel` stops them, and a newer turn's read takes over. */
export interface AssistReadback {
  /** Start reading back the card of the turn whose `turn_complete` is at `turnSeq`. */
  readBack: (turnSeq: number) => void;
  /** Stop every pending read (a new message is being sent). */
  cancel: () => void;
}

/** The reads in progress: from which seq cards are looked for, and which turn's run they wait on. */
interface PendingRead {
  fromSeq: number;
  turnSeq: number;
  timers: ReturnType<typeof setTimeout>[];
}

/**
 * Reads a finished turn's assist card back through `assist.readback` (only the chat's assist cards and
 * whether the run has settled, never the transcript) and puts it in the live transcript, which
 * `chat.get` only seeds once per chat. Called with a turn's `turn_complete` seq when it carries
 * `assistPending` — live, replayed by stream recovery, or seeded from history.
 *
 * One turn is read at a time. A newer turn cancels the older one's timers but reads from the older
 * turn's seq once, so an older card already written (the server writes none after a newer turn starts)
 * still lands; after that read it looks only past the newer turn. Sending a message cancels the timers
 * and keeps that starting seq for the next turn. The reads stop when the turn's card arrives or its run
 * has settled. The card is placed by seq, under its own answer. A chat change or unmount clears every
 * timer and discards any read still in flight, so one chat's card never lands in another.
 */
export function useAssistReadback(options: {
  sessionId: string;
  messagesRef: { readonly current: readonly SSEMessage[] };
  insertMessage: (message: SSEMessage) => void;
}): AssistReadback {
  const { sessionId, messagesRef, insertMessage } = options;
  const queryClient = useQueryClient();
  const orpc = useOrpc();
  const pendingRef = useRef<PendingRead | null>(null);
  /** The oldest seq an earlier, cancelled turn still needed read from. */
  const carryFromRef = useRef<number | null>(null);
  /** Bumped by every stop; a read started under an older value is dropped. */
  const generationRef = useRef(0);

  const stopTimers = useCallback(() => {
    for (const timer of pendingRef.current?.timers ?? []) clearTimeout(timer);
    generationRef.current += 1;
  }, []);

  const cancel = useCallback(() => {
    const pending = pendingRef.current;
    stopTimers();
    if (pending !== null) carryFromRef.current = Math.min(carryFromRef.current ?? pending.fromSeq, pending.fromSeq);
    pendingRef.current = null;
  }, [stopTimers]);

  useEffect(
    () => () => {
      stopTimers();
      pendingRef.current = null;
      carryFromRef.current = null;
    },
    [sessionId, stopTimers],
  );

  const readBack = useCallback(
    (turnSeq: number) => {
      // Already reading this turn back (a live event and a replay can both report it).
      if (pendingRef.current?.turnSeq === turnSeq) return;
      // An older turn may still be reading; this turn's reads take over, from its starting seq.
      cancel();
      const fromSeq = Math.min(carryFromRef.current ?? turnSeq, turnSeq);
      carryFromRef.current = null;
      const generation = generationRef.current;
      const pending: PendingRead = { fromSeq, turnSeq, timers: [] };
      const last = ASSIST_READBACK_DELAYS_MS.length - 1;
      const finish = () => {
        if (pendingRef.current !== pending) return;
        stopTimers();
        pendingRef.current = null;
      };
      pending.timers = ASSIST_READBACK_DELAYS_MS.map((delay, index) =>
        setTimeout(() => {
          const input = { sessionId, afterSeq: pending.fromSeq, turnSeq };
          void queryClient
            .fetchQuery({ ...orpc.assist.readback.queryOptions({ input }), staleTime: 0, gcTime: 0 })
            .then((data) => {
              if (generationRef.current !== generation || pendingRef.current !== pending) return;
              // `insertMessage` ignores a seq the transcript already holds.
              for (const card of newAssistCards(data.cards, messagesRef.current, pending.fromSeq)) insertMessage(card);
              // Anything an older turn wrote is in by now; only this turn's own card is still to come.
              pending.fromSeq = turnSeq;
              const ownCard = data.cards.some((card) => card.seq != null && card.seq > turnSeq);
              if (ownCard || data.settled || index === last) finish();
            })
            .catch(() => {
              if (generationRef.current === generation && index === last) finish();
            });
        }, delay),
      );
      pendingRef.current = pending;
    },
    [queryClient, orpc, sessionId, messagesRef, insertMessage, cancel, stopTimers],
  );

  return useMemo(() => ({ readBack, cancel }), [readBack, cancel]);
}
